import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RISK_COLORS, RISK_LABELS } from "@/lib/agents";
import { RefreshCcw, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [risk, setRisk] = useState("all");
  const [agent, setAgent] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (risk !== "all") params.set("risk", risk);
    if (agent !== "all") params.set("agent_id", agent);
    api.get(`/audit?${params.toString()}`).then((r) => setLogs(r.data.logs));
  };

  useEffect(() => { load(); }, [risk, agent]);

  const filtered = logs.filter((l) =>
    !q || (`${l.action} ${l.detail} ${l.user_email}`.toLowerCase().includes(q.toLowerCase()))
  );

  const AGENT_IDS = ["ceo", "coo", "ciso", "audit", "knowledge", "sales", "marketing", "finance"];

  return (
    <div className="p-8 space-y-6" data-testid="audit-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">REGISTRO INMUTABLE</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Auditoría</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Si no está aquí, no ocurrió. Cada acción de agente queda registrada.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="rounded-none border-[var(--border)]"
          data-testid="btn-refresh-audit"
        >
          <RefreshCcw className="w-3 h-3 mr-2" /> Refrescar
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border border-[var(--border)] bg-[var(--surface)] p-4">
        <div className="col-span-2 flex items-center gap-2">
          <Filter className="w-4 h-4 text-[var(--text-dim)]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar acción, usuario, detalle…"
            className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono"
            data-testid="audit-search"
          />
        </div>
        <Select value={risk} onValueChange={setRisk}>
          <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="audit-risk">
            <SelectValue placeholder="Riesgo" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
            <SelectItem value="all">Todos los riesgos</SelectItem>
            <SelectItem value="low">Bajo</SelectItem>
            <SelectItem value="medium">Medio</SelectItem>
            <SelectItem value="high">Alto</SelectItem>
            <SelectItem value="critical">Crítico</SelectItem>
          </SelectContent>
        </Select>
        <Select value={agent} onValueChange={setAgent}>
          <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="audit-agent">
            <SelectValue placeholder="Agente" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
            <SelectItem value="all">Todos los agentes</SelectItem>
            {AGENT_IDS.map((a) => (
              <SelectItem key={a} value={a}>{a.toUpperCase()}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
        <div className="grid grid-cols-[140px_120px_140px_1fr_100px] label-mono py-3 px-4 border-b border-[var(--border)] bg-[var(--surface-2)]">
          <div>TIMESTAMP</div>
          <div>AGENTE</div>
          <div>ACCIÓN</div>
          <div>DETALLE</div>
          <div className="text-right">RIESGO</div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--text-dim)] font-mono" data-testid="audit-empty">
            No hay eventos con estos filtros.
          </div>
        ) : (
          filtered.map((l) => (
            <div
              key={l.id}
              className="grid grid-cols-[140px_120px_140px_1fr_100px] py-3 px-4 border-b border-[var(--border)] font-mono text-xs hover:bg-[var(--surface-2)]/60"
              data-testid={`audit-row-${l.id}`}
            >
              <div className="text-[var(--text-dim)]">
                {new Date(l.timestamp).toLocaleString("es-ES", {
                  month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
                })}
              </div>
              <div className="uppercase text-[var(--accent)]">{l.agent_id || "—"}</div>
              <div className="text-white">{l.action}</div>
              <div className="text-[var(--text-dim)] truncate">{l.detail}</div>
              <div className="text-right">
                <Badge
                  className="rounded-none font-mono text-[10px]"
                  style={{ background: RISK_COLORS[l.risk] || "#475569", color: "#0B0E14" }}
                >
                  {RISK_LABELS[l.risk] || l.risk}
                </Badge>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
